/**
 * Offline Bootstrap System
 * 
 * Manages session and restaurant data caching for offline PWA cold starts.
 * When the user opens the PWA without internet, this cache allows the app
 * to initialize and work in offline mode.
 */

const BOOTSTRAP_KEY = 'actualfood_offline_bootstrap';
const BOOTSTRAP_MAX_AGE_DAYS = 7;

export interface OfflineBootstrapData {
  userId: string;
  userEmail: string;
  userName?: string;
  restaurantId: string;
  restaurantName: string;
  restaurantSlug?: string;
  cachedAt: string;
}

/**
 * Save bootstrap data for offline cold starts
 * Called when user is online and authenticated
 */
export function saveBootstrapData(data: Omit<OfflineBootstrapData, 'cachedAt'>): void {
  try {
    const bootstrapData: OfflineBootstrapData = {
      ...data,
      cachedAt: new Date().toISOString(),
    };
    localStorage.setItem(BOOTSTRAP_KEY, JSON.stringify(bootstrapData));
    console.log('[OfflineBootstrap] Bootstrap data saved successfully');
  } catch (error) {
    console.error('[OfflineBootstrap] Error saving bootstrap data:', error);
  }
}

/**
 * Retrieve bootstrap data for offline initialization
 * Returns null if no valid cache exists
 */
export function getBootstrapData(): OfflineBootstrapData | null {
  try {
    const stored = localStorage.getItem(BOOTSTRAP_KEY);
    if (!stored) {
      return null;
    }

    const data: OfflineBootstrapData = JSON.parse(stored);
    
    // Validate required fields
    if (!data.userId || !data.userEmail || !data.restaurantId || !data.restaurantName) {
      console.warn('[OfflineBootstrap] Invalid bootstrap data, missing required fields');
      return null;
    }

    return data;
  } catch (error) {
    console.error('[OfflineBootstrap] Error reading bootstrap data:', error);
    return null;
  }
}

/**
 * Check if bootstrap cache is still valid (not expired)
 */
export function isBootstrapValid(): boolean {
  const data = getBootstrapData();
  if (!data) {
    return false;
  }

  try {
    const cachedDate = new Date(data.cachedAt);
    const now = new Date();
    const diffDays = (now.getTime() - cachedDate.getTime()) / (1000 * 60 * 60 * 24);
    
    return diffDays < BOOTSTRAP_MAX_AGE_DAYS;
  } catch {
    return false;
  }
}

/**
 * Clear bootstrap data (called on logout)
 */
export function clearBootstrapData(): void {
  try {
    localStorage.removeItem(BOOTSTRAP_KEY);
    console.log('[OfflineBootstrap] Bootstrap data cleared');
  } catch (error) {
    console.error('[OfflineBootstrap] Error clearing bootstrap data:', error);
  }
}

/**
 * Check if currently offline
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}

/**
 * Check if app can boot in offline mode
 * Returns true if offline and has valid bootstrap cache
 */
export function canBootOffline(): boolean {
  return isOffline() && isBootstrapValid();
}
